import React, { useState } from 'react'
import { X, ChevronDown } from 'lucide-react'
import { Slider } from "../components/ui/slider"

// Add sortBy to the props
export default function FilterMenu({ isOpen, onClose, onApply, sortBy, variant = 'overlay' }) {
  const isSidebar = variant === 'sidebar'
  const [priceRange, setPriceRange] = useState([0, 50000])
  const [guestCount, setGuestCount] = useState('all')
  const [selectedFacilities, setSelectedFacilities] = useState([])
  const [showNearby, setShowNearby] = useState(false)
  const [selectedSort, setSelectedSort] = useState(sortBy || 'recommended')
  const [showAllFacilities, setShowAllFacilities] = useState(false)

  // Add sorting options
  const sortOptions = [
    { value: 'recommended', label: 'Recommended' },
    { value: 'price-low-high', label: 'Price: Low to High' },
    { value: 'price-high-low', label: 'Price: High to Low' }
  ]

  // Add this after the sortOptions array and before the handlers
  const facilities = [
    'Swimming Pool',
    'WiFi',
    'TV',
    'AC',
    'Kitchen',
    'Parking',
    'Private Spa',
    'Games Room',
    'Turf',
    'Meditation Area',
    'Outdoor Lounging Area',
    'Private Bathroom',
    'BBQ Facilities',
    'Balcony',
    'View',
    'Pet Friendly',
    'Gym',
    'Garden',
    'Mountain View',
    'Beach View',
    'Alcohol Allowed',
    'Birthday',
    'Party',
    'Indoor Games',
    'CCTV',
    'Power Backup',
    'Pre Wedding',
    'Wedding',
    'Big Function',
    'Towels',
    'Dinner'
  ];

  const handlePriceRangeChange = (value) => {
    setPriceRange(value)
  }

  const handleFacilityToggle = (facility) => {
    setSelectedFacilities(prev => 
      prev.includes(facility) 
        ? prev.filter(f => f !== facility)
        : [...prev, facility]
    )
  }

  const handleApply = () => {
    onApply({
      priceRange,
      guestCount,
      facilities: selectedFacilities,
      showNearby,
      sortBy: selectedSort
    })
  }

  const handleClear = () => {
    setPriceRange([0, 50000])
    setGuestCount('all')
    setSelectedFacilities([])
    setShowNearby(false)
    setSelectedSort('recommended')
    onApply({
      priceRange: [0, 50000],
      guestCount: 'all',
      facilities: [],
      showNearby: false,
      sortBy: 'recommended'
    })
  }

  if (isSidebar) {
    return (
      <aside className="w-full">
        <div className="sticky top-24 bg-white rounded-2xl border shadow-sm">
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-xl font-semibold">Filters</h2>
          </div>

          <div className="overflow-auto max-h-[calc(100vh-220px)]">
            <div className="p-6 space-y-8">
              {/* Add Sort Options at the top */}
              <div className="space-y-4">
                <h3 className="font-medium text-base">Sort By</h3>
                <div className="grid grid-cols-1 gap-2">
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setSelectedSort(option.value)}
                      className={`h-[44px] px-4 rounded-full border transition-colors text-sm flex items-center ${
                        selectedSort === option.value
                          ? 'bg-black text-white'
                          : 'hover:border-black'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div className="space-y-4">
                <h3 className="font-medium">Price Range</h3>
                <div className="px-2">
                  <Slider
                    value={priceRange}
                    min={0}
                    max={50000}
                    step={1000}
                    onValueChange={handlePriceRangeChange}
                    className="w-full"
                  />
                </div>
                <div className="flex justify-between text-sm">
                  <span>₹{priceRange[0].toLocaleString()}</span>
                  <span>₹{priceRange[1].toLocaleString()}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {[
                    [0, 10000],
                    [10000, 25000],
                    [25000, 50000]
                  ].map(([min, max]) => (
                    <button
                      key={`${min}-${max}`}
                      onClick={() => setPriceRange([min, max])}
                      className={`h-[44px] px-4 rounded-full border transition-colors text-sm flex items-center justify-center ${
                        priceRange[0] === min && priceRange[1] === max
                          ? 'bg-black text-white'
                          : 'hover:border-black'
                      }`}
                    >
                      ₹{min.toLocaleString()} - ₹{max.toLocaleString()}
                    </button>
                  ))}
                </div>
              </div>

              {/* Guest Count */}
              <div className="space-y-4">
                <h3 className="font-medium text-base">Guest Count</h3>
                <div className="grid grid-cols-2 gap-3">
                  {['2+', '4+', '6+', '8+'].map((count) => (
                    <button
                      key={count}
                      onClick={() => setGuestCount(count)}
                      className={`h-[44px] rounded-full border transition-colors text-sm flex items-center justify-center ${
                        guestCount === count ? 'bg-black text-white' : 'hover:border-black'
                      }`}
                    >
                      {count} Guests
                    </button>
                  ))}
                </div>
              </div>

              {/* Facilities */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-base">Facilities</h3>
                  <button
                    onClick={() => setShowAllFacilities(!showAllFacilities)}
                    className="text-sm text-gray-500 flex items-center gap-1 transition-colors hover:text-gray-700"
                  >
                    {showAllFacilities ? 'Show Less' : 'Show More'}
                    <ChevronDown
                      className={`w-4 h-4 transform transition-transform duration-300 ease-in-out ${
                        showAllFacilities ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                </div>
                <div
                  className={`grid grid-cols-2 gap-3 transition-all duration-300 ease-in-out ${
                    showAllFacilities ? 'max-h-[500px]' : 'max-h-[180px]'
                  } overflow-hidden`}
                >
                  {facilities.map((facility) => (
                    <button
                      key={facility}
                      onClick={() => handleFacilityToggle(facility)}
                      className={`h-[44px] rounded-full border transition-colors text-sm flex items-center justify-center ${
                        selectedFacilities.includes(facility)
                          ? 'bg-black text-white border-black'
                          : 'hover:border-black'
                      }`}
                    >
                      <span className="px-2 truncate">{facility}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Nearby Toggle */}
              <div className="space-y-4">
                <button
                  onClick={() => setShowNearby(!showNearby)}
                  className={`w-full h-[44px] rounded-full border transition-colors text-sm flex items-center justify-center ${
                    showNearby ? 'bg-black text-white' : 'hover:border-black'
                  }`}
                >
                  Show Nearby Properties
                </button>
              </div>
            </div>
          </div>

          {/* Bottom Buttons */}
          <div className="sticky bottom-0 left-0 right-0 p-4 bg-white border-t flex gap-4">
            <button
              onClick={handleClear}
              className="flex-1 h-[44px] rounded-full border border-gray-300 hover:bg-gray-50 transition-colors text-sm flex items-center justify-center"
            >
              Clear all
            </button>
            <button
              onClick={handleApply}
              className="flex-1 h-[44px] rounded-full bg-black text-white hover:bg-gray-800 transition-colors text-sm flex items-center justify-center"
            >
              Apply
            </button>
          </div>
        </div>
      </aside>
    )
  }

  return (
    <div
      className={`fixed inset-0 bg-black/50 z-50 transition-opacity duration-300 ${
        isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
      onClick={onClose}
    >
      <div
        className={`fixed bottom-0 right-0 w-full md:w-[400px] bg-white rounded-t-[20px] md:rounded-none transition-transform duration-300 transform ${
          isOpen ? 'translate-y-0' : 'translate-y-full md:translate-x-full'
        }`}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-semibold">Filters</h2>
          <button onClick={onClose} className="p-1">
            <X className="w-6 w-6" />
          </button>
        </div>

        <div className="overflow-auto h-[calc(100vh-180px)] md:h-[calc(100vh-120px)]">
          <div className="p-6 space-y-8">
            {/* Add Sort Options at the top */}
            <div className="space-y-4">
              <h3 className="font-medium text-base">Sort By</h3>
              <div className="grid grid-cols-1 gap-2">
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setSelectedSort(option.value)}
                    className={`h-[44px] px-4 rounded-full border transition-colors text-sm flex items-center ${
                      selectedSort === option.value 
                        ? 'bg-black text-white' 
                        : 'hover:border-black'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Price Range */}
            <div className="space-y-4">
              <h3 className="font-medium">Price Range</h3>
              <div className="px-2">
                <Slider
                  value={priceRange}
                  min={0}
                  max={50000}
                  step={1000}
                  onValueChange={handlePriceRangeChange}
                  className="w-full"
                />
              </div>
              <div className="flex justify-between text-sm">
                <span>₹{priceRange[0].toLocaleString()}</span>
                <span>₹{priceRange[1].toLocaleString()}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  [0, 10000],
                  [10000, 25000],
                  [25000, 50000]
                ].map(([min, max]) => (
                  <button
                    key={`${min}-${max}`}
                    onClick={() => setPriceRange([min, max])}
                    className={`h-[44px] px-4 rounded-full border transition-colors text-sm flex items-center justify-center ${
                      priceRange[0] === min && priceRange[1] === max
                        ? 'bg-black text-white'
                        : 'hover:border-black'
                    }`}
                  >
                    ₹{min.toLocaleString()} - ₹{max.toLocaleString()}
                  </button>
                ))}
              </div>
            </div>

            {/* Guest Count */}
            <div className="space-y-4">
              <h3 className="font-medium text-base">Guest Count</h3>
              <div className="grid grid-cols-2 gap-3">
                {['2+', '4+', '6+', '8+'].map((count) => (
                  <button
                    key={count}
                    onClick={() => setGuestCount(count)}
                    className={`h-[44px] rounded-full border transition-colors text-sm flex items-center justify-center ${
                      guestCount === count ? 'bg-black text-white' : 'hover:border-black'
                    }`}
                  >
                    {count} Guests
                  </button>
                ))}
              </div>
            </div>

            {/* Facilities */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-base">Facilities</h3>
                <button 
                  onClick={() => setShowAllFacilities(!showAllFacilities)}
                  className="text-sm text-gray-500 flex items-center gap-1 transition-colors hover:text-gray-700"
                >
                  {showAllFacilities ? 'Show Less' : 'Show More'}
                  <ChevronDown 
                    className={`w-4 h-4 transform transition-transform duration-300 ease-in-out ${
                      showAllFacilities ? 'rotate-180' : ''
                    }`} 
                  />
                </button>
              </div>
              <div 
                className={`grid grid-cols-2 gap-3 transition-all duration-300 ease-in-out ${
                  showAllFacilities ? 'max-h-[500px]' : 'max-h-[180px]'
                } overflow-hidden`}
              >
                {facilities.map((facility) => (
                  <button
                    key={facility}
                    onClick={() => handleFacilityToggle(facility)}
                    className={`h-[44px] rounded-full border transition-colors text-sm flex items-center justify-center ${
                      selectedFacilities.includes(facility) 
                        ? 'bg-black text-white border-black' 
                        : 'hover:border-black'
                    }`}
                  >
                    <span className="px-2 truncate">{facility}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Nearby Toggle */}
            <div className="space-y-4">
              <button
                onClick={() => setShowNearby(!showNearby)}
                className={`w-full h-[44px] rounded-full border transition-colors text-sm flex items-center justify-center ${
                  showNearby ? 'bg-black text-white' : 'hover:border-black'
                }`}
              >
                Show Nearby Properties
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Buttons */}
        <div className="sticky bottom-0 left-0 right-0 p-4 bg-white border-t flex gap-4">
          <button
            onClick={handleClear}
            className="flex-1 h-[44px] rounded-full border border-gray-300 hover:bg-gray-50 transition-colors text-sm flex items-center justify-center"
          >
            Clear all
          </button>
          <button
            onClick={handleApply}
            className="flex-1 h-[44px] rounded-full bg-black text-white hover:bg-gray-800 transition-colors text-sm flex items-center justify-center"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  )
}